import { batch } from 'react-redux';
import {
	AUTHENTICATION_COMPLETE,
	ERROR_OCCURRED_DURING_LOGOUT,
	LOGIN_COMPLETE,
	LOGIN_FAIL,
	LOGIN_START,
	LOGIN_SUCCESS,
	LOGOUT,
	REGISTRATION_DETAIL_COMPLETE,
	SESSION_UPDATED,
	SESSION_VALID,
	REGISTER_FAIL,
	LOGOUT_START
} from '@comeon/actions-public';
import {
	pushRegistrationGAEvent,
	loginFailure,
	loginSuccess as loginSuccessGTM,
	insertTrackEvent,
	registrationFailure,
	registrationSuccess,
	registrationStart
} from '@comeon/analytics';

import {
	accountGetRisk,
	accountHydrate,
	enableMentorMessageDialog,
	fetchCurrency,
	accountUpdate
} from '~/components/account/ducks/index.js';
import { getSession } from '~/components/account/index.js';
import { bankIdAccountLink } from '../bankid/index.js';
import { clearBankID } from '../bankid/ducks/actions';
import tempSessionIdGetter from '~/utils/deviceId.js';
import {
	getGoogleClientId,
	getToken,
	login as apiLogin,
	prelogin,
	logout,
	postAuthSuccess,
	register,
	sendClientIdForTracking,
	setTermsAndConditions,
	setPrivacyPolicy,
	setSessionTime,
	completeSession
} from '../index.js';
import { syncCookies } from '~/store/ducks/cookies.js';
import { parseResponse } from '~/services/api.js';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import { formSubmitStop } from '~/components/forms/ducks/index.js';
import { hydrateBrand, updateBrand } from '~/components/brand/ducks/index.js';
import { clearCookie, getLocalBTag, getReferrerUrl, setCookie, setPlayerIsGuestCookie } from '~/store/cookies.js';
import { refresh as refreshTranslations } from '~/components/translation/ducks/index.ts';
import { refresh as refreshResources } from '~/components/resource/ducks/index.js';
import getCredentials from '../get-credentials.js';
import MOJEID_STATUSES from '../moje-id/constants';
import POPUP_ERRORS from '../register/popup-error-list.js';
import { resetSupportWidget, setVIPChatTagCVValue, showSupportDialogBox } from '~/components/support/ducks/index.js';
import { clearNotifications } from '../../account/my-notifications/ducks/my-notifications.js';
import * as AN from './action-names';
import { SIGN_OUT_TYPES } from './constants';
import { STATUS as CAPTCHA_STATUS } from '../recaptcha/constants.js';
import {
	NOTIFICATION_TYPE,
	sendAppBridgeEvent,
	showNativeNotification,
	WEB_EVENTS
} from '~/components/app-bridge/index.js';
import { getTemporaryPlayer } from '../temporary-account/api/index.js';
import { cddnRegisterCall, getCddnUrlCall } from '../external-providers/cddn/api';
import { ROUTE_ACCOUNT_DEPOSIT } from '~/components/router/constants';
import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../../error-handling/error-reporter-domain';
import { endGamePlay } from '~/components/account/reality-check/ducks';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import { isNativeNotificationEnabled } from '~/components/app-bridge/app-properties';

export const updateCredentials = payload => {
	return {
		type: AN.UPDATE_CREDENTIALS,
		payload
	};
};

export const missingDataSuccess = payload => {
	return {
		type: AN.MISSING_DATA_SUCCESS,
		payload
	};
};

export const persistValues = payload => {
	return {
		type: AN.PERSIST_VALUES,
		payload
	};
};

export const clearPersistedValues = () => {
	return {
		type: AN.CLEAR_PERSISTED_VALUES
	};
};

export const loginSuccess = (payload, uiConfig, config) => {
	return async (dispatch, getState) => {
		const { frame } = getState().analytics;
		// gtm event
		// passing a callback function to handle the PIXEL_CV value
		loginSuccessGTM(
			{
				method: config?.method || 'UsernamePw',
				frame
			},
			eventInfo => {
				const parsedData = JSON.parse(eventInfo.replace(/'/g, '"'));
				if (parsedData.pixel_cv) {
					dispatch(setVIPChatTagCVValue(Number.parseInt(parsedData.pixel_cv, 10)));
				}
			}
		);

		// inform mobile apps about login success
		sendAppBridgeEvent(WEB_EVENTS.LOGIN, [{ authenticated: true }]);

		// FIXME: Remove this hack, if authenticated true is part of BE response, we dont need this condition
		// we can dispatch the payload to redux
		if (
			payload.shouldShowComplianceMessage ||
			payload.shouldShowPrivacyPolicyMessage ||
			payload.showAccountVerification ||
			payload.showDepositLimits ||
			payload.showExtendedVerification ||
			payload.shouldShowPersonalDetailsConfirmation ||
			payload.mfaEnabled ||
			payload.showPhoneVerification ||
			payload.showResponsibleGamingPage ||
			payload.showPlayerDetailsPage ||
			payload.requiresLoginOnLugas ||
			payload.shouldShowWagerAndTimeLimitPage ||
			payload.shouldShowDepositLimitPage ||
			payload.shouldShowBalanceLimitPage ||
			payload.shouldShowSessionLimitPage
		) {
			dispatch({
				type: LOGIN_SUCCESS,
				payload: { ...payload, loadingAuth: false }
			});
			// will be used for extended verification
			if (payload.encryptedPlayerId) {
				dispatch(
					accountUpdate({
						id: payload.encryptedPlayerId
					})
				);
			}
			return;
		}
		// need to hydrate brand in case user's franchise changes after logging in
		// ex: a german user logs in sweden, needs to see german welcome page, hence need await
		const { payload: brand } = await dispatch(hydrateBrand());

		const waitForAccountHydrate = brand.isDanishFranchise;

		// for Denmark we need nemid status and wee need to wait for account hydrated
		if (waitForAccountHydrate) {
			await dispatch(accountHydrate());
		}
		// FIXME
		payload.authenticated = true; // eslint-disable-line no-param-reassign
		dispatch({
			type: LOGIN_SUCCESS,
			payload
		});

		dispatch(syncCookies());

		if (!waitForAccountHydrate) {
			dispatch(accountHydrate());
		}
		// need to hydrate account for compliance features to be shown, can be async

		const { withinSidebar = false } = uiConfig || {};

		if (getState().bankId.accountLink) {
			const accountLink = await bankIdAccountLink(getState().bankId.accountLink);
			if (accountLink.error || accountLink !== 'OK') {
				dispatch(
					showNotification({
						message: getState().translations.BANKID_ACCOUNT_LINK_ERROR,
						color: 'red',
						autoHide: true,
						openInSidear: withinSidebar
					})
				);
			} else {
				dispatch(
					showNotification({
						message: getState().translations.BANKID_ACCOUNT_LINK_SUCCESS,
						color: 'green',
						autoHide: true,
						openInSidear: withinSidebar
					})
				);
			}
			dispatch(clearBankID());
		}

		if (getState().brand.showMentorMessages) {
			dispatch(enableMentorMessageDialog());
		}

		// send clientId for tracking
		await sendClientIdForTracking();

		// removing RCAccepted cookie for german reality check
		if (getState().brand.isGermanRegulatedFranchise) {
			clearCookie('RCAccepted');
		}

		return dispatch({
			type: LOGIN_COMPLETE
		});
	};
};

export const handleLoginResult = (response, dispatch, uiConfig, config) => {
	if (response.status === 'FAILURE' && response?.result?.showSchufaReverification === 'true') {
		const { result } = response;
		return dispatch(updateCredentials({ ...result, showSchufaReverification: true, loadingAuth: false }));
	}
	if (response.status === 'FAILURE') {
		const { withinSidebar = false } = uiConfig || {};
		if (!response.result) {
			loginFailure({
				method: config?.method || 'UsernamePw',
				error: `${response.errorCode} - ${response.errorDescription}`
			});
		}
		return dispatch({
			type: LOGIN_FAIL,
			payload: { ...response, withinSidebar, loadingAuth: false }
		});
	}

	// Save login type to preselect tab on next login
	window.localStorage.setItem('lastLoginMethod', config?.method || 'UsernamePw');

	const { result } = response;
	// when temporary account germany, this flag mandatory shall come as true
	if (result?.temporaryPlayer?.temporary) {
		if (result?.temporaryPlayer?.expired === false) {
			// welcome screen modal with remaining time
			dispatch(updateCredentials({ ...result, showTempAccountLogin: true }));
		}
		if (result?.temporaryPlayer?.expired) {
			// your time is over modal temporary expired
			dispatch(updateCredentials({ ...result, showTempAccountScreen: true }));
		}
	}

	// When user exited schufa on registration and login
	if (result.showExtendedVerification && config.isGermanRegulatedFranchise) {
		dispatch(updateCredentials({ ...result, showTempAccountLogin: true }));
	}
	if (result.mustChangePw) {
		dispatch(updateCredentials({ ...result, loadingAuth: false }));
		return dispatch({ type: AN.MUST_CHANGE_PASSWORD });
	}

	return dispatch(loginSuccess(result || {}, uiConfig, config));
};

export const login = uiConfig => {
	return async (dispatch, getState) => {
		const { authentication } = getState();
		let captchaToken = '';
		// if captcha token is not present in redux store, use the API
		if (!authentication.captchaWhitelisted) {
			captchaToken = authentication.captchaToken;
		}

		dispatch({
			type: LOGIN_START
		});

		const credentials = getCredentials(captchaToken, authentication);
		const response = await apiLogin({ type: authentication?.type, ...credentials }, false, () => {
			dispatch(clearPersistedValues());
		});

		// reset the captcha after login goes through
		if (!authentication.captchaWhitelisted) {
			window.grecaptcha?.reset();
		}

		return handleLoginResult(response, dispatch, uiConfig, {
			isGermanRegulatedFranchise: getState().brand.isGermanRegulatedFranchise
		});
	};
};

export const socialLogin = (uiConfig, credentials, type) => {
	return async (dispatch, getState) => {
		const { authentication } = getState();
		let captchaToken = '';
		// if captcha token is not present in redux store, use the API
		if (!authentication.captchaWhitelisted) {
			captchaToken = authentication.captchaToken;
		}

		dispatch({
			type: LOGIN_START
		});

		const payload = getCredentials(captchaToken, credentials);
		const response = await apiLogin(
			{
				type,
				...payload
			},
			true,
			() => {
				dispatch(clearPersistedValues());
			}
		);
		// reset the captcha after login goes through
		if (!authentication.captchaWhitelisted) {
			window.grecaptcha?.reset();
		}

		return handleLoginResult(response, dispatch, uiConfig, {
			method: type.toLowerCase()
		});
	};
};

export const getPreloginStatus = async email => {
	return prelogin({ email });
};

const resetSessionTimers = timeLimitTimers => {
	timeLimitTimers.forEach(timeOutID => {
		clearTimeout(timeOutID);
	});
};

export const setPostAuthLocation = location => ({
	type: AN.SET_POST_AUTH_LOCATION,
	payload: location
});

export const setDeviceSessionId = deviceSession => ({
	type: AN.SET_DEVICE_SESSION,
	payload: deviceSession
});

export const clearPostAuthLocation = () => setPostAuthLocation(null);

export const showUsernameAndPasswordLogin = () => {
	return {
		type: AN.SHOW_USERNAME_PASSWORD_LOGIN
	};
};

export const showBankidLogin = () => {
	return {
		type: AN.SHOW_BANKID_LOGIN
	};
};

export const showPAGLogin = () => {
	return {
		type: AN.SHOW_PAG_LOGIN
	};
};

export const showPhoneLogin = () => {
	return {
		type: AN.SHOW_PHONE_LOGIN
	};
};

export const loadCaptcha = () => {
	return {
		type: AN.LOAD_RECAPTCHA
	};
};

export const loadSeon = () => {
	return {
		type: AN.LOAD_SEON
	};
};
export const grecaptchaExecute = () => {
	window.grecaptcha?.execute();
	return {
		type: AN.RECAPTCHA_EXECUTE,
		payload: {
			captchaWhitelisted: false,
			captchaStatus: CAPTCHA_STATUS.IN_PROGRESS
		}
	};
};

export const whiteListCaptcha = () => {
	return { type: AN.RECAPTCHA_WHITELIST };
};

export const grecaptchaCallback = captchaToken => dispatch =>
	dispatch({
		type: AN.RECAPTCHA_CALLBACK,
		payload: { captchaToken, captchaStatus: CAPTCHA_STATUS.COMPLETE }
	});

export const resetCaptcha = () => {
	window.grecaptcha?.reset();

	return {
		type: AN.RECAPTCHA_RESET
	};
};

// simulates the login form submit action, cant be used for flows needed for captcha
export const loginAction = (payload, uiConfig) => {
	return async dispatch => {
		// set email and password in redux state
		if (payload) {
			dispatch(updateCredentials(payload));
		}
		return dispatch(login(uiConfig));
	};
};

export const authenticationComplete = from => {
	return {
		type: AUTHENTICATION_COMPLETE,
		payload: { from }
	};
};

export const showWelcomePopup = (bool = false, welcomeOptions = {}) => {
	return {
		type: AN.SHOW_WELCOME_POPUP,
		payload: {
			showWelcomePopup: bool,
			welcomeOptions
		}
	};
};

export const signout = (
	{ message = '', messageType = 'red', buttonLink = '', buttonText = '', autoHide = true, openInSidebar = false },
	signOutType = SIGN_OUT_TYPES.USER_ACTION
) => {
	return async (dispatch, getState) => {
		dispatch({
			type: LOGOUT_START
		});

		const { timeLimit, realityCheck } = getState();
		const { brand } = getState();
		const timeLimitTimers = timeLimit?.timeLimitTimers ?? [];
		if (message) {
			const messageTextKeys = ['CRUKS_EXCLUDED_LOGOUT'];
			if (messageTextKeys.includes(message)) {
				dispatch(
					showQueuedNotification({
						messageKey: message,
						color: messageType,
						autoHide,
						buttonLink,
						buttonText,
						openInSidebar
					})
				);
			} else {
				dispatch(
					showQueuedNotification({
						message,
						color: messageType,
						autoHide,
						buttonLink,
						buttonText,
						openInSidebar
					})
				);
			}
		}

		if (brand.isNetherlandsRegulatedFranchise) {
			dispatch(
				showWelcomePopup(true, {
					title: 'USER_LIMITS_TITLE',
					subTitle: 'USER_LIMITS_SUB_TITLE',
					tracking: false,
					showIcon: false,
					isWelcome: false,
					showCloseBtn: false,
					showActionBtn: true
				})
			);

			await new Promise(resolve => {
				setTimeout(resolve, 2000);
			});
		}

		const response = await logout();
		if (response.status === 'FAILURE') {
			dispatch({
				type: ERROR_OCCURRED_DURING_LOGOUT
			});
		}
		clearCookie('verificationRequired');

		dispatch(syncCookies());
		dispatch(fetchCurrency(true));
		dispatch(hydrateBrand());
		if (brand.isGuestPlayer) {
			dispatch(
				updateBrand({
					siteType: 'REGULAR',
					isGuestPlayer: false
				})
			);
		}

		// clearing out inbox notifications for user
		dispatch(clearNotifications());

		// reset the support widget
		dispatch(resetSupportWidget());

		// call reset for timers
		resetSessionTimers(timeLimitTimers);
		clearTimeout((realityCheck || {}).timerId); // clear realityCheck timer
		if (brand.isNetherlandsRegulatedFranchise) {
			dispatch(endGamePlay());
		}

		if (signOutType === SIGN_OUT_TYPES.USER_ACTION) {
			// Notify native app clients about the logout action.
			sendAppBridgeEvent(WEB_EVENTS.LOGOUT);
		} else {
			sendAppBridgeEvent(WEB_EVENTS.SESSION_TERMINATED, [{ type: SIGN_OUT_TYPES.SESSION_ENDED }]);
		}

		return dispatch({ type: LOGOUT });
	};
};

export const validateSession = (
	{ signoutMessage = {}, isInit = false, loginMethod = '', skipAnalytics = false } = {},
	force = false
) => {
	return async (dispatch, getState) => {
		const state = getState();
		const isRegistration = state.account?.isRegistration;
		const { isValidateSessionEnabled, captchaToken, registrationPixelType = '' } = state.authentication;
		// if login call with captcha is ongoing, return
		const isLoginInProgress = !!captchaToken;
		if ((!force && !isValidateSessionEnabled) || isLoginInProgress) {
			return;
		}
		dispatch({ type: AN.SESSION_VALIDATE });

		const { authenticated } = state.authentication;

		// TODO: refactor this whole mess. doesnt need to be so complicated
		let serverLoggedIn = false;
		let serverLogoutMsg = '';
		if (isInit) {
			serverLoggedIn = authenticated;
		} else {
			try {
				const session = await getSession(
					state.brand?.isNetherlandsRegulatedFranchise ? state.account?.id : null
				);
				serverLoggedIn = session.map.authenticated === 'true';
				serverLogoutMsg = session.map.message;
			} catch (e) {
				reportException(e, {
					domain: ErrorReporterDomain.AUTHENTICATION,
					extra: {
						type: 'status-api-error',
						url: window.location
					}
				});
			}
		}

		if (isInit || serverLoggedIn !== authenticated) {
			if (serverLoggedIn) {
				if (getState().brand.isGuestPlayer) {
					setPlayerIsGuestCookie();
				}
				const {
					payload: { franchiseName }
				} = await dispatch(hydrateBrand());
				if (!isInit) {
					dispatch(refreshTranslations());
					// when player logs in, call set session time, loggedInTime needs to be set
					setSessionTime();
				}
				dispatch(refreshResources());
				// Weird way to reconcile that its login, but if session is not activate on load and not registration, then its a login
				if (!isRegistration && !isInit) {
					dispatch({
						type: LOGIN_SUCCESS,
						payload: {}
					});
				}
				postAuthSuccess({
					isRegistration,
					isComingFromInit: isInit,
					callbackFunction: eventInfo => {
						const parsedData = JSON.parse(eventInfo.replace(/'/g, '"'));
						if (parsedData.pixel_cv) {
							dispatch(setVIPChatTagCVValue(Number.parseInt(parsedData.pixel_cv, 10)));
						}
					},
					onSuccessCallback: () => {
						dispatch(clearPersistedValues());
					}
				});
				const { payload: account } = await dispatch(accountHydrate());

				// removing RCAccepted cookie for german reality check
				if (state.brand.isGermanRegulatedFranchise && !isInit) {
					clearCookie('RCAccepted');
				}

				// inform mobile apps about login success
				sendAppBridgeEvent(WEB_EVENTS.LOGIN, [{ authenticated: true }]);

				if (account.isRegistration && !account.hydrated) {
					// inform mobile apps about register event
					sendAppBridgeEvent(WEB_EVENTS.REGISTRATION_COMPLETE);
					if (state.brand.isSwedishRegulatedFranchise) {
						pushRegistrationGAEvent({
							step: `registration-complete${registrationPixelType}`,
							playerId: account.id,
							franchiseName,
							clonableProps: ['pixel_depositAmount'],
							clearCookies: true,
							type: loginMethod
						});
					}
				} else if (!skipAnalytics) {
					loginSuccessGTM({
						newEvent: true,
						frame: state.analytics.frame,
						method: loginMethod
					});
				}

				dispatch(accountGetRisk());
				return dispatch({
					type: SESSION_VALID,
					payload: { loadingAuth: false }
				});
			}

			if (!serverLoggedIn && !authenticated) {
				/**
				 * if no local and remote session then don't
				 * perform any side effect. If local session is missing but app state
				 * is not logged out it's bug. signout action should be fired only if
				 * local and remote session mismatch
				 */

				return dispatch({
					type: AN.NO_SESSION_FOUND
				});
			}
			const message = signoutMessage.message ? signoutMessage : { message: serverLogoutMsg };

			if (getState().brand.enableTempAccountFlow) {
				dispatch(signout(message, SIGN_OUT_TYPES.SESSION_ENDED));

				const encryptedPlayerId = getState()?.account?.id;
				if (encryptedPlayerId) {
					const responseTemporaryPlayer = await getTemporaryPlayer(encryptedPlayerId);
					if (
						responseTemporaryPlayer.status === 'SUCCESS' &&
						responseTemporaryPlayer?.result?.temporary === true &&
						responseTemporaryPlayer?.result?.expired
					) {
						dispatch(
							updateCredentials({
								isTemporaryExpired: responseTemporaryPlayer.result.expired
							})
						);
					}
				}
			} else {
				dispatch(signout(message, SIGN_OUT_TYPES.SESSION_ENDED));
			}

			return dispatch({
				type: AN.SESSION_EXPIRED
			});
		}

		if (getState().brand.isGuestPlayer) {
			setPlayerIsGuestCookie();
		}

		return dispatch({
			type: AN.SESSION_UNCHANGED
		});
	};
};

export const disableValidateSession = () => {
	return {
		type: AN.DISABLE_VALIDATE_SESSION
	};
};

export const enableValidateSession = () => {
	return {
		type: AN.ENABLE_VALIDATE_SESSION
	};
};

export const acceptTermsConditions = () => {
	return async (dispatch, getState) => {
		dispatch({ type: AN.ACCEPT_TERMS_AND_CONDITIONS });

		dispatch(
			updateCredentials({
				isComplianceTermsAccepted: true
			})
		);
		const payload = {
			encryptedPlayerId: getState()?.account?.id ?? null
		};
		const response = await setTermsAndConditions(payload);
		// if t&c is updated, check if session can be activated
		if (response.status === 'SUCCESS') {
			// show the correct following screen
			dispatch(
				updateCredentials({
					...response?.result,
					shouldShowComplianceMessage: false,
					loadingAuth: !!response?.result?.sessionId
				})
			);
			if (response?.result?.sessionId) {
				dispatch(enableValidateSession());
				return dispatch(validateSession({}, true));
			}
		}

		// failure case here
	};
};

export const acceptPrivacyPolicy = () => {
	return async (dispatch, getState) => {
		dispatch({ type: AN.ACCEPT_PRIVACY_POLICY });

		dispatch(
			updateCredentials({
				isPrivacyPolicyAccepted: true
			})
		);

		const payload = {
			encryptedPlayerId: getState()?.account?.id ?? null
		};
		const response = await setPrivacyPolicy(payload);
		// if t&c is updated, check if session can be activated
		if (response.status === 'SUCCESS') {
			// show the correct following screen
			dispatch(
				updateCredentials({
					...response?.result,
					shouldShowPrivacyPolicyMessage: false,
					loadingAuth: !!response?.result?.sessionId
				})
			);
			if (response?.result?.sessionId) {
				dispatch(enableValidateSession());
				return dispatch(validateSession({}, true));
			}
		}

		// failure case here
	};
};
export const updateSession = authenticated => {
	return {
		type: SESSION_UPDATED,
		payload: { authenticated }
	};
};

export const requestRegistrationToken = uiConfig => {
	return async (dispatch, getState) => {
		dispatch({
			type: AN.REGISTRATION_BASIC_START
		});

		const { email, password, acceptAccurateInfo, acceptMarketingOffers, callingCode, phoneNumber } =
			getState().authentication;

		const { withinSidebar = false } = uiConfig || {};

		const payload = {
			email,
			password,
			acceptAccurateInfo,
			acceptMarketingOffers,
			phoneNumber: phoneNumber?.replace(/^0/, ''),
			callingCode
		};

		const response = await getToken(payload);

		dispatch(formSubmitStop());

		if (response.error) {
			return batch(() => {
				dispatch(
					showNotification({
						message: response.error.errorDescription,
						color: 'red',
						autoHide: true,
						openInSidebar: withinSidebar
					})
				);
				return dispatch({
					type: AN.REGISTRATION_BASIC_FAIL,
					payload: response.error
				});
			});
		}
		return batch(() => {
			dispatch({
				type: AN.REGISTRATION_BASIC_SUCCESS,
				payload: response
			});

			dispatch(syncCookies());

			return dispatch({
				type: AN.REGISTRATION_BASIC_COMPLETE,
				payload: response
			});
		});
	};
};

export const registrationBasic = (payload, uiConfig) => {
	return dispatch => {
		dispatch(updateCredentials(payload));

		return dispatch(requestRegistrationToken(uiConfig));
	};
};

export const initiateCddnVerification = ({ isCddnMockEnabled }) => {
	return async dispatch => {
		try {
			const { status, result } = await getCddnUrlCall();

			if (status === 'SUCCESS') {
				if (isCddnMockEnabled) {
					setCookie('cddnOperatorKey', 'mock');

					dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'pending' });
				}
				return result;
			}

			if (status === 'FAILURE') {
				dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'failed' });
			}
		} catch {
			dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'failed' });
		}
	};
};

export const registrationCddn = () => {
	return async dispatch => {
		try {
			const deviceId = tempSessionIdGetter();
			const btag = getLocalBTag();
			const referrerUrl = getReferrerUrl();
			const googleID = getGoogleClientId();

			const credentials = {
				deviceId,
				clientId: googleID,
				...(btag ? { btag } : {}),
				...(referrerUrl ? { referrerUrl } : {})
			};

			registrationStart({ method: 'CDDN - Registration', newEvent: true });

			const { status, result, errorDescription, errorCode } = await cddnRegisterCall(credentials);

			if (status === 'SUCCESS') {
				dispatch(
					accountUpdate({
						...(result.encryptedPlayerId ? { id: result.encryptedPlayerId } : {})
					})
				);
				dispatch(updateCredentials({ ...result, credentials }));
				dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'succeeded' });
				registrationSuccess({
					method: 'CDDN - Registration',
					encryptedPID: result.encryptedPlayerId ?? null,
					newEvent: true
				});
			}

			if (status === 'FAILURE') {
				dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'failed' });
				registrationFailure({ method: 'CDDN - Registration', error: errorCode, newEvent: true });

				if (isNativeNotificationEnabled()) {
					showNativeNotification(NOTIFICATION_TYPE.CDDN_REGISTER_ERROR, errorDescription);
				} else if (errorCode === 'CDDN_VERIFICATION_ERROR') {
					dispatch(
						showQueuedNotification({
							message: errorDescription,
							color: 'red',
							buttonTextKey: 'BUTTON_CUSTOMER_SUPPORT',
							buttonCB: () => {
								dispatch(showSupportDialogBox(true));
							}
						})
					);
				} else {
					dispatch(
						showQueuedNotification({
							message: errorDescription,
							color: 'red'
						})
					);
				}
			}
		} catch {
			dispatch({ type: AN.SET_CDD_REGISTER_STATUS, payload: 'failed' });
			registrationFailure({ method: 'CDDN - Registration', error: 'CDDN try catch failure', newEvent: true });
		}
	};
};

export const extendedVerification = payload => {
	return {
		type: AN.REGISTRATION_EXTENDED_VERIFICATION,
		payload
	};
};

export const extendedVerificationComplete = payload => {
	return dispatch => {
		dispatch({
			type: AN.UPDATE_CREDENTIALS,
			payload
		});
	};
};

let birthdayMissMatches = 0;
const MAX_SCHUFA_ATTEMPTS = 3;

export const updateRegistration = (uiConfig, resetForm) => {
	// TODO: Need to refactor
	return async (dispatch, getState) => {
		dispatch({
			type: AN.REGISTRATION_DETAIL_START
		});

		const {
			authentication: {
				from,
				exitPath,
				email,
				password,
				firstName,
				lastName,
				address,
				postalCode,
				city,
				callingCode,
				phoneNumber,
				year,
				month,
				day,
				dkCprNumber,
				dkGamblingRangeId,
				personNumber,
				gender,
				country,
				currency,
				state,
				acceptTerms,
				acceptMarketingOffers,
				acceptAccurateInfo,
				voucherCode,
				pesel,
				documentType,
				documentNumber,
				timeLimitsDay,
				timeLimitsMonth,
				wagerLimitsDay,
				wagerLimitsMonth,
				birthPlace,
				citizenship,
				familyName,
				inputTransactionId,
				inputProvider,
				occupation,
				industry,
				registrationType,
				deviceSession,
				xSellReferrer,
				sourceFranchise,
				caseId
			},
			brand: {
				isDanishFranchise,
				isPolishFranchise,
				isCanadianFranchise,
				isGermanRegulatedFranchise,
				isSwedishRegulatedFranchise,
				removeMarketingConsentCheckbox,
				isOntarioRegulatedFranchise,
				isSeonDeviceIdEnabled,
				xSellFastRegistration
			},
			translations: { SCHUFA_SUPPORT_URL }
		} = getState();

		const deviceId = tempSessionIdGetter();
		const btag = getLocalBTag();
		const referrerUrl = getReferrerUrl();
		const googleID = getGoogleClientId();
		const credentials = {
			email,
			password,
			firstName,
			lastName,
			address,
			postalCode,
			city,
			callingCode,
			phoneNumber: phoneNumber?.replace(/^0/, ''),
			year,
			month,
			day,
			...(isDanishFranchise && dkCprNumber
				? {
						dkCprNumber:
							`0${day}`.slice(-2) +
							`0${month}`.slice(-2) +
							`00${year}`.slice(-2) +
							`0000${dkCprNumber}`.slice(-4)
					}
				: {}),
			...(isDanishFranchise ? { dkGamblingRangeId } : {}),
			...(isSwedishRegulatedFranchise ? { personNumber } : {}),
			gender,
			country,
			currency,
			state,
			voucherCode,
			...(isPolishFranchise
				? {
						...(documentNumber ? { documentType, documentNumber } : {}),
						timeLimitsDay,
						timeLimitsMonth,
						wagerLimitsDay,
						wagerLimitsMonth,
						inputTransactionId,
						inputProvider
					}
				: {}),
			deviceId,
			...(isSeonDeviceIdEnabled ? { deviceSession } : {}),
			acceptMarketingOffers: removeMarketingConsentCheckbox
				? acceptTerms
				: isCanadianFranchise
					? acceptMarketingOffers
					: !acceptMarketingOffers,
			...(xSellFastRegistration ? { acceptAccurateInfo } : {}),
			...(isOntarioRegulatedFranchise ? { citizenship, occupation, industry } : {}),
			...(isGermanRegulatedFranchise
				? {
						birthPlace,
						citizenship,
						familyName
					}
				: {}),
			...(pesel ? { personNumber: pesel } : {}),
			...(btag ? { btag } : {}),
			...(referrerUrl ? { referrerUrl } : {}),
			clientId: googleID,
			type: registrationType,
			...(caseId ? { caseId } : {})
		};

		const { withinSidebar = false } = uiConfig || {};

		try {
			// rest/register/player
			let response = await register(credentials, { xSellReferrer, sourceFranchise });
			dispatch(formSubmitStop());
			response = parseResponse(response);
			if (response.error) {
				const isBirthdayMismatches =
					response.error.errorCode === 'PlayerException_SCHUFA_BIRTHDATE_MISMATCH' ||
					response.error.errorCode === 'PlayerException_SCHUFA_VALIDATION_ERROR';
				if (isBirthdayMismatches) {
					birthdayMissMatches += 1;
				}

				if (birthdayMissMatches >= MAX_SCHUFA_ATTEMPTS) {
					dispatch(
						showNotification({
							messageKey: 'SCHUFA_BIRTHDAY_MISMATCH_THIRD_TIME_TEXT',
							buttonLink: SCHUFA_SUPPORT_URL,
							linkCB: () => {
								insertTrackEvent({
									category: 'Account Verification',
									action: 'Click - Go to Schufa portal',
									label: 'Schufa'
								});
							},
							buttonTextKey: 'SCHUFA_BIRTHDAY_MISMATCH_THIRD_TIME_BUTTON',
							color: 'red',
							openInSidebar: withinSidebar,
							buttonBottom: true
						})
					);
				} else if (POPUP_ERRORS[response.error.errorCode]) {
					dispatch({ type: REGISTER_FAIL, payload: response.error });
				} else {
					dispatch(
						showNotification({
							message: response.error.errorDescription,
							color: 'red',
							autoHide: !isBirthdayMismatches,
							openInSidebar: withinSidebar
						})
					);
				}
			}

			dispatch(
				accountUpdate({
					isRegistration: true,
					...(response.encryptedPlayerId ? { id: response.encryptedPlayerId } : {}) // will be used for extended verification
				})
			);

			if (response.shouldShowWelcomePageDetails) {
				dispatch(
					updateCredentials({
						shouldShowWelcomePageDetails: response.shouldShowWelcomePageDetails
					})
				);
			}

			// Tip: if you want to try local extended verification, just comment response.showExtendedVerification
			if (response.showExtendedVerification) {
				// send the encrypted pid to app
				if (response.encryptedPlayerId) {
					sendAppBridgeEvent(WEB_EVENTS.ENCRYPTED_PID_UPDATED_FOR_EV, [
						{ encryptedPlayerId: response.encryptedPlayerId }
					]);
				}
				if (response?.showDepositLimits && response?.depositLimitCountryCode) {
					dispatch(
						updateCredentials({
							showDepositLimits: response.showDepositLimits,
							depositLimitCountryCode: response?.depositLimitCountryCode
						})
					);
				}
				// add post auth location for when registration is completed
				dispatch(
					updateCredentials({
						postAuthLocation: {
							pathname: ROUTE_ACCOUNT_DEPOSIT,
							state: { from, exitPath, referrer: 'register' }
						}
					})
				);
				return dispatch(extendedVerification(true));
			}
			if (response.showResponsibleGamingPage) {
				dispatch(
					updateCredentials({
						showResponsibleGamingPage: response.showResponsibleGamingPage
					})
				);
			}
			if (
				response.showDepositLimits &&
				(isDanishFranchise || isGermanRegulatedFranchise || isSwedishRegulatedFranchise)
			) {
				return dispatch({
					type: AN.SHOW_DEPOSIT_LIMITS,
					payload: {
						showDepositLimits: true,
						depositLimitCountryCode: response.depositLimitCountryCode,
						casinoAndSportsbookEnabled: response.casinoAndSportsbookEnabled,
						postAuthLocation: {
							pathname: ROUTE_ACCOUNT_DEPOSIT,
							state: { from, exitPath, referrer: 'register' }
						}
					}
				});
			}

			if (response.showPhoneVerification) {
				return dispatch({
					type: AN.SHOW_SMS_VERIFICATION,
					payload: {
						showPhoneVerification: true,
						postAuthLocation: {
							pathname: ROUTE_ACCOUNT_DEPOSIT,
							state: { from, exitPath, referrer: 'register' }
						}
					}
				});
			}
			if (response?.shouldShowWagerAndTimeLimitPage) {
				dispatch(
					updateCredentials({
						shouldShowWagerAndTimeLimitPage: response?.shouldShowWagerAndTimeLimitPage,
						postAuthLocation: {
							pathname: ROUTE_ACCOUNT_DEPOSIT,
							state: { from, exitPath, referrer: 'register' }
						}
					})
				);
			}

			dispatch(syncCookies());

			if (response.status === 'SUCCESS') {
				// inform mobile apps about register event
				sendAppBridgeEvent(WEB_EVENTS.REGISTRATION_COMPLETE);
			}

			dispatch({
				type: REGISTRATION_DETAIL_COMPLETE
			});
			if (response && !response.error && resetForm) {
				// BANJO-4641: Need to call this twice https://github.com/jaredpalmer/formik-persist/issues/16
				resetForm({});
				resetForm({});
			}
			if (response.status === 'SUCCESS') {
				completeSession(); // for sites without sessionCriteria
			}
			return dispatch({
				type: AN.UPDATE_CREDENTIALS,
				payload: {
					authenticated: response.map.authenticated,
					postAuthLocation: {
						pathname: ROUTE_ACCOUNT_DEPOSIT,
						state: { from, exitPath, referrer: 'register' }
					}
				}
			});
		} catch {
			dispatch(formSubmitStop());
		}
	};
};

export const registrationDetail = (payload, uiConfig, resetForm) => {
	return dispatch => {
		dispatch({
			type: AN.UPDATE_CREDENTIALS,
			payload
		});

		return dispatch(updateRegistration(uiConfig, resetForm));
	};
};

export const registrationUpdateCredentials = payload => {
	return dispatch => {
		dispatch({
			type: AN.UPDATE_CREDENTIALS,
			payload
		});
	};
};

export const nonceLogin = nonce => {
	return async dispatch => {
		dispatch({
			type: LOGIN_START
		});
		const response = await apiLogin({ nonce }, false, () => {
			dispatch(clearPersistedValues());
		});
		return handleLoginResult(response, dispatch);
	};
};

export const cancelLogin = () => {
	return {
		type: AN.CANCEL_LOGIN
	};
};

export const marketingCommunicationUpdated = () => ({
	type: AN.SHOULD_SHOW_MARKETING_COMMUNICATION_SETTINGS,
	payload: {
		shouldShowMarketingCommunicationSettings: false
	}
});

export const showWelcomeScreen = (bool = true) => {
	return {
		type: AN.SHOW_WELCOME_SCREEN,
		payload: bool
	};
};

export const setExitPath = location => {
	return {
		type: AN.SET_AUTH_EXITPATH,
		payload: location
	};
};

export const updateAcceptMarketingOffers = update => ({
	type: AN.UPDATE_ACCEPT_MARKETING_OFFERS,
	payload: update
});

export const loginFailed = result => ({
	type: LOGIN_FAIL,
	payload: {
		errorCode: 'ACCOUNT_INFORMATION_NO_RESET_PW',
		result
	}
});
export const loginFailedLugas = result => ({
	type: LOGIN_FAIL,
	payload: {
		...result
	}
});
export const registrationPhoneResend = payload => {
	return {
		type: AN.REGISTRATION_PHONE_RESEND,
		payload
	};
};

export const trustlyPopup = (response, dispatch) => {
	loginFailure({
		newEvent: true,
		error: `${response.errorCode} - ${response.errorDescription}`
	});
	return dispatch({
		type: LOGIN_FAIL,
		payload: {
			...response,
			withinSidebar: true,
			trustlyPopup: true
		}
	});
};

export const toggleMojeID = (boolean = false) => ({
	type: AN.SHOW_MOJE_ID,
	payload: boolean
});

export const mojeIDStatus = (status = MOJEID_STATUSES.START) => ({
	type: AN.MOJEID_STATUS,
	payload: status
});

export const toggleTransactionLink = boolean => ({
	type: AN.TOGGLE_TRANSACTION_LINK,
	payload: boolean
});

export const closeLugasLogin = () => {
	return {
		type: AN.CLOSE_LUGAS_LOGIN
	};
};

export const triggerFbLogin = status => {
	return {
		type: AN.TRIGGER_FB_LOGIN,
		payload: status
	};
};

export const setSocialLoginType = type => {
	return {
		type: AN.SET_SOCIAL_LOGIN_TYPE,
		payload: type
	};
};

export const setShouldShowMarketingConsent = payload => {
	return {
		type: AN.SET_SHOULD_SHOW_MARKETING_CONSENT,
		payload
	};
};
